import {
	data as json,
	type HeadersFunction,
	type MetaFunction,
	useSearchParams,
} from 'react-router'
import { ButtonLink } from '#app/components/button.tsx'
import { FullScreenVideoEmbed } from '#app/components/fullscreen-video-embed.tsx'
import { Grid } from '#app/components/grid.tsx'
import { BlogSection } from '#app/components/sections/blog-section.tsx'
import { HeaderSection } from '#app/components/sections/header-section.tsx'
import { HeroSection } from '#app/components/sections/hero-section.tsx'
import { H2, H3, H6, Paragraph } from '#app/components/typography.tsx'
import { getBlogRecommendations } from '#app/utils/blog.server.ts'
import { reuseUsefulLoaderHeaders } from '#app/utils/misc.ts'
import { getServerTimeHeader } from '#app/utils/timing.server.ts'
import { type KCDHandle } from '#app/types.ts'
import { type Route } from './+types/about'

export const handle: KCDHandle = {
	getSitemapEntries: () => [{ route: '/about' }],
}
export async function loader({ request }: Route.LoaderArgs) {
	const timings = {}
	const socialMetas = (
		await import('#app/og/page-meta.server.ts')
	).buildPageSocialMetasForRequest(request, {
		title: 'About Hamidul Islam',
		description: 'Get to know Hamidul Islam',
		keywords: 'about, hamid, hamidul islam, hamidx, hamidul',
		image: '/images/heroes/about.jpg',
		imageAlt: 'About Hamidul Islam',
	})

	return json(
		{
			blogRecommendations: await getBlogRecommendations({ request, timings }),
			socialMetas,
		},
		{
			headers: {
				'Cache-Control': 'private, max-age=3600',
				Vary: 'Cookie',
				'Server-Timing': getServerTimeHeader(timings),
			},
		},
	)
}

export const headers: HeadersFunction = reuseUsefulLoaderHeaders

export const meta: MetaFunction<typeof loader> = ({ data }) =>
	data?.socialMetas ?? []

function AboutIndex({ loaderData }: Route.ComponentProps) {
	const { blogRecommendations } = loaderData
	const [searchParams] = useSearchParams()

	return (
		<>
			<HeroSection
				title="Hi, I'm Hamidul - I help businesses ship systems that grow."
				subtitle="I turn conversion leaks, expensive media, and brittle integrations into product systems customers feel and P&Ls notice."
				imageProps={{
					src: '/images/heroes/about.png',
					alt: 'Standing desk and systems illustration',
					className: 'object-contain',
					style: { aspectRatio: '1' },
				}}
				imageSize="large"
				arrowUrl="#about-me"
				arrowLabel="Get to know more about me"
				action={
					<div className="mr-auto flex flex-col gap-4">
						<ButtonLink to="https://cal.com/hamidxcal" variant="primary">
							Schedule a call
						</ButtonLink>
						<ButtonLink to="/work" variant="secondary" prefetch="intent">
							View work
						</ButtonLink>
					</div>
				}
			/>

			<Grid as="main" className="mt-16 mb-24 lg:mb-48">
				<div className="col-span-full">
					<FullScreenVideoEmbed
						autoplay={searchParams.has('autoplay')}
						posterSrc="/images/about/consultation-video-cover.jpg"
						posterAlt="Hamidul presenting a product consultation talk to a seated audience"
						videoSrc="/videos/workshop-video.mp4"
						title="Product consultation"
					/>
				</div>

				<div className="col-span-full mt-16" id="about-me">
					<H2 className="mb-10">{`How I got here.`}</H2>
				</div>

				<div className="col-span-full lg:col-span-6 lg:col-start-1">
					<img
						src="/images/about/standing-desk-4x3.jpg"
						alt="Hamidul standing at a desk building product systems"
						className="mb-8 aspect-[4/3] w-full rounded-lg object-cover object-center"
						loading="lazy"
					/>
					<H3 className="mb-4">Product systems at the desk</H3>
					<Paragraph className="mb-8">
						{`
              I've been obsessed with understanding how things actually work -
              not just that code prints a number, but how the CPU, memory, and
              binary execution produce that result. That curiosity drives how I
              build product systems today.
            `}
					</Paragraph>
					<Paragraph>
						{`
              As a performance and infrastructure-minded product engineer, I care
              about queries, caches, Core Web Vitals, and shipping ownership with
              a team - so products stay fast when the business starts to grow.
            `}
					</Paragraph>
				</div>

				<div className="col-span-full mt-12 lg:col-span-4 lg:col-start-8 lg:mt-0">
					<img
						src="/images/hamid-portrait.webp"
						alt="Portrait of Hamidul Islam"
						width={768}
						height={1024}
						className="mb-8 aspect-[3/4] w-full max-w-xs rounded-lg object-cover object-[center_20%]"
						loading="lazy"
						decoding="async"
					/>
					<p className="text-primary mb-8 text-2xl font-medium">Hamid</p>
					<H6 as="h3" className="mb-4">
						How I help businesses
					</H6>
					<ul className="text-secondary list-inside list-disc space-y-3 text-lg">
						<li>Find the revenue leak or cost spike</li>
						<li>Design the system that removes it</li>
						<li>Ship something the team can own</li>
						<li>Keep it fast when traffic gets real</li>
					</ul>
				</div>

				<div className="col-span-full mt-20 lg:mt-28">
					<img
						src="/images/about/hardware-lab-3x2.jpg"
						alt="Hamidul soldering ESP32 and robotics hardware with low-level code on screen"
						className="mb-10 aspect-[3/2] w-full rounded-lg object-cover object-center"
						loading="lazy"
					/>
				</div>
				<div className="col-span-full lg:col-span-7">
					<H3 className="mb-4">
						Hardware interest - sensor to software to actuator
					</H3>
					<Paragraph className="mb-8">
						{`
              Away from the browser, I dig into embedded and low-level work:
              soldering boards, wiring robots, flashing ESP32s, and writing the
              C/C++ that talks to real hardware.
            `}
					</Paragraph>
					<Paragraph>
						{`
              Camera, radar, microphone, sensors, ADC, MCU, GPIO, PWM, SPI, I²C,
              UART, CAN - the full path from physical signal to software decision
              to actuator. That same systems thinking shows up in the product
              platforms I ship for businesses.
            `}
					</Paragraph>
				</div>
				<div className="col-span-full mt-10 lg:col-span-4 lg:col-start-9 lg:mt-0">
					<H6 as="h3" className="mb-4">
						What I'm exploring
					</H6>
					<ul className="text-secondary list-inside list-disc space-y-3 text-lg">
						<li>ESP32 / MCU firmware</li>
						<li>Sensors, ADC, and signal paths</li>
						<li>Robotics and actuators</li>
						<li>Bare-metal and RTOS habits</li>
					</ul>
				</div>
			</Grid>

			<div className="mb-24">
				<HeaderSection
					title="Here are some of the values I live by."
					subTitle="How I show up on a team."
				/>
				<div className="mx-10vw mt-16">
					<div className="mx-auto grid max-w-7xl gap-12 lg:grid-cols-3">
						<div>
							<H3 className="mb-4">Depth</H3>
							<Paragraph>
								{`I dig until I understand the mechanism - then I can optimize and teach it.`}
							</Paragraph>
						</div>
						<div>
							<H3 className="mb-4">Collaboration</H3>
							<Paragraph>
								{`Great systems are built with people. I communicate early, review carefully, and leave codebases kinder.`}
							</Paragraph>
						</div>
						<div>
							<H3 className="mb-4">Shipping</H3>
							<Paragraph>
								{`Ideas without delivery don't count. I take problems from ambiguity to production.`}
							</Paragraph>
						</div>
					</div>
				</div>
			</div>

			<div className="mb-24">
				<BlogSection
					articles={blogRecommendations}
					title="Blog recommendations"
					description="Prepared especially for you."
				/>
			</div>
		</>
	)
}

export default AboutIndex
